import { Brand, getTokensByBrand } from '@newfold/tokens'
import { createTheme, PaletteOptions } from '@mui/material/styles'
import createPalette, { PaletteMode } from '@mui/material/styles/createPalette'
import networkSolutionColors from '../assets/styles/tokens.json'

const getThemeByBrand = (brand: Brand, mode: PaletteMode = 'light') => {
  const { color } = (brand === 'networksolutions') ? networkSolutionColors : getTokensByBrand(brand)

  const palette: PaletteOptions = {
    ...(mode === 'light'
      ? {
          text: {
            primary: 'rgb(255,255,255)',
            disabled: color.spectrum.neutral[400],
            secondary: color.spectrum.neutral[500], // TODO: should this be secondary 600ish?
          },
          action: {
            hoverOpacity: 0,
            disabled: color.spectrum.neutral[400],
          },
          common: {
            white: color.semantic.neutral[0],
            black: color.semantic.neutral[1000],
          },

          primary: {
            ...color.semantic.primary,
            A100: color.semantic.primary[200],
            A200: color.semantic.primary[400],
            A400: color.semantic.primary[600],
            A700: color.semantic.primary[900],

            main: color.semantic.primary.main,
            light: color.semantic.primary.light,
            dark: color.semantic.primary.dark,
          },
          secondary: {
            ...color.semantic.secondary,
            A100: color.semantic.secondary[200],
            A200: color.semantic.secondary[400],
            A400: color.semantic.secondary[600],
            A700: color.semantic.secondary[900],

            main: color.semantic.secondary.main,
            light: color.semantic.secondary.light,
            dark: color.semantic.secondary.dark,
          },
          info: {
            ...color.semantic.info,
            A100: color.semantic.info[200],
            A200: color.semantic.info[400],
            A400: color.semantic.info[600],
            A700: color.semantic.info[900],

            main: color.semantic.info.main,
            light: color.semantic.info.light,
            dark: color.semantic.info.dark,
          },
          success: {
            ...color.semantic.success,
            A100: color.semantic.success[200],
            A200: color.semantic.success[400],
            A400: color.semantic.success[600],
            A700: color.semantic.success[900],

            main: color.semantic.success.main,
            light: color.semantic.success.light,
            dark: color.semantic.success.dark,
          },
          error: {
            ...color.semantic.error,
            A100: color.semantic.error[200],
            A200: color.semantic.error[400],
            A400: color.semantic.error[600],
            A700: color.semantic.error[900],

            main: color.semantic.error.main,
            light: color.semantic.error.light,
            dark: color.semantic.error.dark,
          },
          warning: {
            ...color.semantic.warning,
            A100: color.semantic.warning[200],
            A200: color.semantic.warning[400],
            A400: color.semantic.warning[600],
            A700: color.semantic.warning[900],

            main: color.semantic.warning.main,
            light: color.semantic.warning.light,
            dark: color.semantic.warning.dark,
          },
          grey: {
            ...color.spectrum.neutral,
            A100: color.spectrum.neutral[200],
            A200: color.spectrum.neutral[400],
            A400: color.spectrum.neutral[600],
            A700: color.spectrum.neutral[900],
          },
        }
      : // DARK MODE
        {
          ...createPalette({ mode }),
          background: {
            default: color.semantic.secondary[900],
            paper: color.semantic.secondary[900],
          },
          divider: color.semantic.secondary[300],
          text: {
            primary: color.semantic.neutral[0],
            secondary: color.semantic.secondary[100],
          },
          // action: {
          //   hoverOpacity: 0,
          //   disabled: grey[400],
          // },
          common: {
            white: color.semantic.neutral[0],
            black: color.semantic.neutral[1000],
          },
          primary: {
            ...color.semantic.primary,
            // light: color.semantic.primary[100],
            // main: color.semantic.primary[300],
            // dark: color.semantic.primary[500],
          },
          secondary: {
            ...color.semantic.secondary,
            // light: color.semantic.secondary[100],
            // main: color.semantic.secondary[300],
            // dark: color.semantic.secondary[500],
          },
          info: {
            ...color.semantic.info,
          },
          success: {
            ...color.semantic.success,
          },
          error: {
            ...color.semantic.error,
          },
          warning: {
            ...color.semantic.warning,
          },
          grey: {
            ...color.spectrum.neutral,
            A100: color.spectrum.neutral[200],
            A200: color.spectrum.neutral[400],
            A400: color.spectrum.neutral[600],
            A700: color.spectrum.neutral[900],
          },
        }),
  }

  return createTheme({
    components: {
      // Card components
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: '16px 24px 16px 16px'
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderColor: color.spectrum.neutral[300],
            borderRadius: '12px',
            backgroundColor: color.semantic.neutral[0],
          }
        }
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            padding: '16px',
            backgroundColor: color.semantic.neutral[50],
          },
          title: {
            color: color.semantic.primary.main,
          }
        }
      },
      MuiCardActions: {
        styleOverrides: {
          root: {
            padding: '8px 16px',
          }
        }
      },
      
      // Button components
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '6px',
            padding: '10px 16px',
            fontWeight: 700,
            fontSize: '14px',
            textTransform: 'none',
            boxShadow: 'none !important',
          },
          contained: {
            backgroundColor: color.semantic.primary.main,
            color: color.semantic.neutral[0],
            '&:hover': {
              backgroundColor: color.semantic.primary.dark,
            },
            '&.Mui-disabled': {
              backgroundColor: color.spectrum.neutral[200],
              color: color.spectrum.neutral[500],
            }
          },
          outlined: {
            borderColor: color.semantic.primary.main,
            color: color.semantic.primary.main,
            '&:hover': {
              borderColor: color.semantic.primary.dark,
              backgroundColor: color.semantic.primary[50],
            },
            '&.Mui-disabled': {
              borderColor: color.spectrum.neutral[300],
              color: color.spectrum.neutral[500],
            }
          },
          text: {
            color: color.semantic.primary.main,
            '&:hover': {
              backgroundColor: color.semantic.primary[50],
            },
            '&.Mui-disabled': {
              color: color.spectrum.neutral[500],
            }
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: color.semantic.primary.main,
            '&:hover': {
              backgroundColor: color.semantic.primary[50],
            },
            '&.Mui-disabled': {
              color: color.spectrum.neutral[400],
            }
          }
        }
      },
      
      // Menu components
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            margin: '5px 10px',
            padding: '8px 16px',
            '&:hover': {
              backgroundColor: color.semantic.primary[50],
            },
            '&.Mui-selected': {
              backgroundColor: color.semantic.primary[100],
              '&:hover': {
                backgroundColor: color.semantic.primary[200],
              }
            }
          }
        }
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: '8px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          }
        }
      },
      
      // Form components
      MuiOutlinedInput: {
        styleOverrides: {
          multiline: {
            padding: '0',
            height: 'fit-content'
          },
          root: {
            background: color.semantic.neutral[0],
            borderRadius: '6px',
            borderColor: color.spectrum.neutral[300],
            height: 40,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: color.semantic.primary.main,
              borderWidth: '2px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: color.semantic.primary[300],
            },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: color.semantic.error.main,
            },
            '&.Mui-disabled': {
              backgroundColor: color.spectrum.neutral[100],
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: color.spectrum.neutral[200],
              }
            }
          },
          input: {
            padding: '10px 16px',
            fontSize: '14px',
            color: color.semantic.neutral[900],
            '&::placeholder': {
              color: color.spectrum.neutral[500],
              opacity: 1,
            }
          },
          notchedOutline: {
            borderColor: color.spectrum.neutral[300],
          }
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '14px',
            fontWeight: 600,
            color: color.semantic.neutral[900],
            marginBottom: '0.7em',
            '&.Mui-focused': {
              color: color.semantic.primary.main,
            },
            '&.Mui-error': {
              color: color.semantic.error.main,
            },
            '&.Mui-disabled': {
              color: color.spectrum.neutral[500],
            }
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: '12px',
            marginTop: '4px',
            '&.Mui-error': {
              color: color.semantic.error.main,
            }
          }
        }
      },
      
      // Select component
      MuiSelect: {
        defaultProps: {
          MenuProps: {
            PaperProps: {
              sx: {
                borderRadius: '0 0 12px 12px',
                marginTop: '4px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              },
            },
          },
        },
        styleOverrides: {
          icon: {
            color: color.semantic.primary.main,
          }
        }
      },
      
      // Checkbox and Radio components
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: color.spectrum.neutral[400],
            '&.Mui-checked': {
              color: color.semantic.primary.main,
            },
            '&:hover': {
              backgroundColor: color.semantic.primary[50],
            },
            '&.Mui-disabled': {
              color: color.spectrum.neutral[300],
            }
          }
        }
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: color.spectrum.neutral[400],
            '&.Mui-checked': {
              color: color.semantic.primary.main,
            },
            '&:hover': {
              backgroundColor: color.semantic.primary[50],
            },
            '&.Mui-disabled': {
              color: color.spectrum.neutral[300],
            }
          }
        }
      },
      
      // Switch component
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 42,
            height: 26,
            padding: 0,
            '& .MuiSwitch-switchBase': {
              padding: 2,
              '&.Mui-checked': {
                transform: 'translateX(16px)',
                color: color.semantic.neutral[0],
                '& + .MuiSwitch-track': {
                  backgroundColor: color.semantic.primary.main,
                  opacity: 1,
                },
              },
            },
            '& .MuiSwitch-thumb': {
              width: 22,
              height: 22,
            },
            '& .MuiSwitch-track': {
              borderRadius: 13,
              backgroundColor: color.spectrum.neutral[400],
            }
          }
        }
      },
      
      // Tabs components
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            color: color.spectrum.neutral[700],
            '&.Mui-selected': {
              color: color.semantic.primary.main,
            }
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: color.semantic.primary.main,
          }
        }
      },
      
      // Progress indicators
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            color: color.semantic.primary.main,
          }
        }
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            backgroundColor: color.spectrum.neutral[200],
            borderRadius: 5,
          },
          bar: {
            backgroundColor: color.semantic.primary.main,
            borderRadius: 5,
          }
        }
      },
      
      // Dialog components
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '12px',
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
          }
        }
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            color: color.semantic.primary.main,
            fontSize: '18px',
            fontWeight: 600,
          }
        }
      },
      
      // Alert component
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
          },
          standardSuccess: {
            backgroundColor: color.semantic.success[50],
            color: color.semantic.success[800],
            '& .MuiAlert-icon': {
              color: color.semantic.success.main,
            }
          },
          standardError: {
            backgroundColor: color.semantic.error[50],
            color: color.semantic.error[800],
            '& .MuiAlert-icon': {
              color: color.semantic.error.main,
            }
          },
          standardWarning: {
            backgroundColor: color.semantic.warning[50],
            color: color.semantic.warning[800],
            '& .MuiAlert-icon': {
              color: color.semantic.warning.main,
            }
          },
          standardInfo: {
            backgroundColor: color.semantic.info[50],
            color: color.semantic.info[800],
            '& .MuiAlert-icon': {
              color: color.semantic.info.main,
            }
          }
        }
      },
      
      // List components
      MuiListItem: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: color.semantic.primary[50],
              '&:hover': {
                backgroundColor: color.semantic.primary[100],
              }
            }
          }
        }
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: color.semantic.primary[50],
            },
            '&.Mui-selected': {
              backgroundColor: color.semantic.primary[100],
              '&:hover': {
                backgroundColor: color.semantic.primary[200],
              }
            }
          }
        }
      },
      
      // Paper component
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: color.semantic.neutral[0],
            borderRadius: '8px',
          },
          outlined: {
            borderColor: color.spectrum.neutral[300],
          }
        }
      },
      
      // Divider component
      MuiDivider: {
        styleOverrides: {
          root: {
            backgroundColor: color.spectrum.neutral[200],
          }
        }
      },
      
      // Tooltip component
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: color.semantic.neutral[900],
            fontSize: '12px',
            padding: '8px 12px',
            borderRadius: '4px',
          }
        }
      },
      
      // Chip component
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: color.semantic.primary[50],
            color: color.semantic.primary[800],
            '&.MuiChip-outlined': {
              backgroundColor: 'transparent',
              borderColor: color.semantic.primary[300],
            },
            '&.Mui-disabled': {
              backgroundColor: color.spectrum.neutral[100],
              color: color.spectrum.neutral[500],
              opacity: 0.7,
            }
          },
          deleteIcon: {
            color: color.semantic.primary[400],
            '&:hover': {
              color: color.semantic.primary[600],
            }
          }
        }
      }
    },
    palette: {
      mode: 'light',
      primary: {
        main: color.semantic.primary.main,
        dark: color.semantic.primary.dark,
        light: color.semantic.primary.light,
      },
      secondary: {
        main: color.semantic.secondary.main,
        dark: color.semantic.secondary.dark,
        light: color.semantic.secondary.light,
      },
      text: {
        primary: color.semantic.neutral[900],
        secondary: color.semantic.neutral[700],
      },
    },
    typography: {
      fontFamily: 'Inter',
      subtitle2: {
        fontSize: 14,
        fontWeight: 500,
      },
      body1: {
        fontSize: 16,
        fontWeight: 400,
      },
      body2: {
        fontSize: 14,
        fontWeight: 400,
        lineHeight: '1.5em'
      },
      h6: {
        fontSize: 14,
        fontWeight: 600,
      },
      h3: {
        fontSize: 28,
        fontWeight: 600,
      },
      h5: {
        fontSize: 14,
        fontWeight: 600,
      },
      button: {
        fontWeight: 400,
      },
    },
  })
}

// TODO: replace this with a better global function or method to safely return brand
const getBrand = () => {
  const hostname = window.location.hostname
  const domainParts = hostname.split('.')
  // TODO:
  const domain = domainParts.slice(-2).join('.') // Get the last two parts of the domain
  switch (domain) {
    case 'web.com':
      return 'web'
    case 'networksolutions.com':
    case 'netsol.com':
      return 'networksolutions'
    case 'bluehost.com':
      return 'bluehost'
    case 'hostgator.com':
      return 'hostgator'
    case 'domain.com':
      return 'domain'
    case 'register.com':
      return 'register'
    default:
      return 'web'
  }
}

const getTheme = () => getThemeByBrand(getBrand())

export const theme = getTheme()
